import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type {
  SupportSeverity,
  SupportTicketDetail,
  SupportTicketKind,
} from "@compliance-sage/contracts";
import { api } from "../../api/client";
import { Modal } from "../../components/Modal";
import { Button, ErrorBanner, Field, Select, TextArea, TextInput } from "../../components/ui";
import { useMutation } from "../../hooks/useMutation";
import { useI18n } from "../../i18n";
import { AttachedContextPanel } from "./AttachedContextPanel";

const KINDS: SupportTicketKind[] = ["SYSTEM_ERROR", "DISPUTED_RESULT", "QUESTION"];
const SEVERITIES: SupportSeverity[] = ["BLOCKING", "DEGRADED", "MINOR"];

/**
 * The report form. It is deliberately short: the reporter picks a kind, says
 * what they expected and what happened, and the server derives everything else
 * from the review and finding ids passed in here. That is what removes the
 * round of follow-up questions an emailed report always needs.
 */
export function ReportProblemDialog({
  open,
  onClose,
  reviewId,
  findingId,
  defaultKind = "SYSTEM_ERROR",
  defaultSubject = "",
  title,
}: {
  open: boolean;
  onClose: () => void;
  reviewId?: string;
  findingId?: string;
  defaultKind?: SupportTicketKind;
  defaultSubject?: string;
  title?: string;
}) {
  const { t, te } = useI18n();
  const [kind, setKind] = useState<SupportTicketKind>(defaultKind);
  const [severity, setSeverity] = useState<SupportSeverity>("DEGRADED");
  const [subject, setSubject] = useState(defaultSubject);
  const [expected, setExpected] = useState("");
  const [observed, setObserved] = useState("");
  const [created, setCreated] = useState<SupportTicketDetail | null>(null);
  // The footer button infers its review from the route, which can name a review
  // this reporter cannot read (a stale link, another tenant's id). The context
  // preview is the check: if the server will not resolve it, the anchor is
  // dropped so the report still sends instead of failing on a 404.
  const [anchorUsable, setAnchorUsable] = useState(true);
  useEffect(() => {
    setAnchorUsable(true);
  }, [reviewId, findingId]);
  const anchoredReviewId = anchorUsable ? reviewId : undefined;

  const submit = useMutation(
    useCallback(
      () =>
        api.createSupportTicket({
          kind,
          severity,
          subject,
          expected: expected.trim() || undefined,
          observed,
          reviewId: anchoredReviewId,
          findingId: anchoredReviewId ? findingId : undefined,
          pagePath: window.location.pathname,
        }),
      [kind, severity, subject, expected, observed, anchoredReviewId, findingId],
    ),
    t("errSupportSubmit"),
  );

  const close = () => {
    // Reset so reopening the dialog does not resurrect a sent report.
    setCreated(null);
    setKind(defaultKind);
    setSeverity("DEGRADED");
    setSubject(defaultSubject);
    setExpected("");
    setObserved("");
    submit.setError(null);
    onClose();
  };

  const send = async () => {
    const ticket = await submit.mutate();
    if (ticket) setCreated(ticket);
  };

  const canSend = subject.trim().length > 0 && observed.trim().length > 0 && !submit.loading;

  if (created) {
    return (
      <Modal
        open={open}
        onClose={close}
        size="md"
        title={t("supportSubmitted")}
        footer={
          <Button variant="secondary" onClick={close}>
            {t("close")}
          </Button>
        }
      >
        <p className="text-sm text-slate-600">
          {t("supportSubmittedHelp", { reference: created.reference })}
        </p>
        <Link
          to={`/support/${created.id}`}
          onClick={close}
          className="mt-3 inline-block text-sm font-medium text-brand-700 hover:text-brand-900"
        >
          {created.subject} →
        </Link>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      title={title ?? t("reportProblemTitle")}
      description={t("reportProblemHelp")}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {t("cancel")}
          </Button>
          <Button onClick={() => void send()} disabled={!canSend}>
            {submit.loading ? t("loading") : t("supportSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ErrorBanner message={submit.error} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("supportKind")}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as SupportTicketKind)}>
              {KINDS.map((value) => (
                <option key={value} value={value}>
                  {te("supportTicketKind", value)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("supportSeverityLabel")}>
            <Select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as SupportSeverity)}
            >
              {SEVERITIES.map((value) => (
                <option key={value} value={value}>
                  {te("supportSeverity", value)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label={t("supportSubject")}>
          <TextInput
            value={subject}
            maxLength={200}
            onChange={(e) => setSubject(e.target.value)}
          />
        </Field>
        <Field label={t("supportExpected")}>
          <TextArea
            value={expected}
            maxLength={4000}
            placeholder={t("supportExpectedPlaceholder")}
            onChange={(e) => setExpected(e.target.value)}
          />
        </Field>
        <Field label={t("supportObserved")}>
          <TextArea
            value={observed}
            maxLength={4000}
            placeholder={t("supportObservedPlaceholder")}
            onChange={(e) => setObserved(e.target.value)}
          />
        </Field>
        {anchoredReviewId ? (
          // Anchored to a review, so the real values can be shown rather than
          // promised: the reporter sees the run, the stage, and the source set
          // that will reach the inbox with their sentence.
          <AttachedContextPanel
            reviewId={anchoredReviewId}
            findingId={findingId}
            onUnavailable={() => setAnchorUsable(false)}
          />
        ) : (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-semibold text-slate-600">{t("supportAttachedContext")}</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              {t("supportAttachedContextHelp")}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
